import {
  Component,
  inject,
  OnInit,
  AfterViewInit,
  DestroyRef,
  signal,
  viewChild,
  TemplateRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  TabsComponent,
  TabItem,
  TableComponent,
  BadgeComponent,
  ButtonComponent,
  ColumnConfig,
  ColumnType,
  LoadingDotsComponent
} from 'htx-ui-library';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '../../../../organisms/confirmation-dialog/confirmation-dialog.component';
import {
  DiscoveryFormDialogComponent,
  DiscoveryFormResult
} from '../discovery-form-dialog/discovery-form-dialog.component';
import { BdrsFormDialogComponent } from '../bdrs-form-dialog/bdrs-form-dialog.component';
import { NavigationService } from '../../../../../services/navigation.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { InfrastructureService } from '../../../../../services/infrastructure/infrastructure.service';
import { HealthService } from '../../../../../services/health/health.service';
import {
  DiscoveryOrganization,
  BdrsEntry,
  BdrsMappingRequest,
  WalletEntry
} from '../../../../../services/infrastructure/infrastructure.model';
import { SystemHealth, HealthStatus } from '../../../../../services/health/health.model';

@Component({
  selector: 'app-infrastructure-page',
  imports: [
    CommonModule,
    TranslocoDirective,
    TranslocoPipe,
    TabsComponent,
    TableComponent,
    BadgeComponent,
    ButtonComponent,
    LoadingDotsComponent,
    ProtectedContentComponent
  ],
  templateUrl: './infrastructure-page.component.html',
  styleUrl: './infrastructure-page.component.scss'
})
export class InfrastructurePageComponent implements OnInit, AfterViewInit {
  private navigationService = inject(NavigationService);
  private infrastructureService = inject(InfrastructureService);
  private healthService = inject(HealthService);
  private notificationService = inject(NotificationService);
  private transloco = inject(TranslocoService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  requiredRoles = this.navigationService.getAccessRoles('admin/infrastructure');

  activeTabId = signal<string>('discovery-service');

  tabs: TabItem[] = [
    { id: 'discovery-service', label: 'Discovery Service' },
    { id: 'dim-wallet', label: 'Dim Wallet' },
    { id: 'bdrs', label: 'BDRS' }
  ];

  // Health status
  healthMap = signal<Record<string, HealthStatus>>({});

  // Discovery
  discoveryData = signal<DiscoveryOrganization[]>([]);
  discoveryLoading = signal<boolean>(false);
  discoveryColumns: ColumnConfig<DiscoveryOrganization>[] = [];

  // BDRS
  bdrsData = signal<BdrsEntry[]>([]);
  bdrsLoading = signal<boolean>(false);
  bdrsColumns: ColumnConfig<BdrsEntry>[] = [];

  // Wallet
  walletData = signal<WalletEntry[]>([]);
  walletLoading = signal<boolean>(false);
  walletColumns: ColumnConfig<WalletEntry>[] = [];

  discoveryActionTemplate = viewChild<TemplateRef<{ $implicit: DiscoveryOrganization }>>(
    'discoveryActionTemplate'
  );
  bdrsActionTemplate = viewChild<TemplateRef<{ $implicit: BdrsEntry }>>('bdrsActionTemplate');

  ngOnInit(): void {
    this.loadHealth();
    this.loadDiscovery();
  }

  ngAfterViewInit(): void {
    this.transloco
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.initColumns());
  }

  onTabChange(tabId: string): void {
    this.activeTabId.set(tabId);
    if (tabId === 'discovery-service' && this.discoveryData().length === 0) {
      this.loadDiscovery();
    } else if (tabId === 'bdrs' && this.bdrsData().length === 0) {
      this.loadBdrs();
    } else if (tabId === 'dim-wallet' && this.walletData().length === 0) {
      this.loadWallet();
    }
  }

  getHealthStatus(systemName: string): HealthStatus | undefined {
    return this.healthMap()[systemName];
  }

  getDiscovery(row: unknown): DiscoveryOrganization {
    return row as DiscoveryOrganization;
  }

  getBdrs(row: unknown): BdrsEntry {
    return row as BdrsEntry;
  }

  private loadHealth(): void {
    this.healthService.getSystemsHealth().subscribe({
      next: (systems: SystemHealth[]) => {
        const map: Record<string, HealthStatus> = {};
        systems.forEach((s) => (map[s.name] = s.status));
        this.healthMap.set(map);
      }
    });
  }

  private loadDiscovery(): void {
    this.discoveryLoading.set(true);
    this.infrastructureService.getDiscoveryOrganizations().subscribe({
      next: (data) => {
        this.discoveryData.set(data);
        this.discoveryLoading.set(false);
      },
      error: () => this.discoveryLoading.set(false)
    });
  }

  private loadBdrs(): void {
    this.bdrsLoading.set(true);
    this.infrastructureService.getBdrsEntries().subscribe({
      next: (data) => {
        this.bdrsData.set(data);
        this.bdrsLoading.set(false);
      },
      error: () => this.bdrsLoading.set(false)
    });
  }

  private loadWallet(): void {
    this.walletLoading.set(true);
    this.infrastructureService.getWalletEntries().subscribe({
      next: (data) => {
        this.walletData.set(data);
        this.walletLoading.set(false);
      },
      error: () => this.walletLoading.set(false)
    });
  }

  // ── Discovery CRUD ───────────────────────────────────────────────────────────

  addDiscovery(): void {
    this.dialog
      .open(DiscoveryFormDialogComponent, { data: { mode: 'add' }, autoFocus: false })
      .afterClosed()
      .pipe(filter((result): result is DiscoveryFormResult => !!result))
      .subscribe((result) => {
        this.infrastructureService
          .createDiscoveryOrganization({
            bpnl: result.bpnl,
            organizationName: result.organizationName,
            protocolUrl: result.connectorUrl,
            useCases: result.useCases
          })
          .subscribe({
            next: () => {
              this.notificationService.showSuccess('dsaManagement.infrastructure.notifications.created');
              this.loadDiscovery();
            },
            error: () =>
              this.notificationService.showError('dsaManagement.infrastructure.notifications.createError')
          });
      });
  }

  editDiscovery(organization: DiscoveryOrganization): void {
    this.dialog
      .open(DiscoveryFormDialogComponent, {
        data: { mode: 'edit', organization },
        autoFocus: false
      })
      .afterClosed()
      .pipe(filter((result): result is DiscoveryFormResult => !!result))
      .subscribe((result) => {
        this.infrastructureService
          .updateDiscoveryOrganization(organization.bpnl, {
            organizationName: result.organizationName,
            protocolUrl: result.connectorUrl,
            useCases: result.useCases
          })
          .subscribe({
            next: () => {
              this.notificationService.showSuccess('dsaManagement.infrastructure.notifications.updated');
              this.loadDiscovery();
            },
            error: () =>
              this.notificationService.showError('dsaManagement.infrastructure.notifications.updateError')
          });
      });
  }

  deleteDiscovery(organization: DiscoveryOrganization): void {
    this.confirmDelete(organization.organizationName || organization.bpnl).subscribe(() => {
      this.infrastructureService.deleteDiscoveryOrganization(organization.bpnl).subscribe({
        next: () => {
          this.notificationService.showSuccess('dsaManagement.infrastructure.notifications.deleted');
          this.loadDiscovery();
        },
        error: () =>
          this.notificationService.showError('dsaManagement.infrastructure.notifications.deleteError')
      });
    });
  }

  // ── BDRS CRUD ────────────────────────────────────────────────────────────────

  addBdrs(): void {
    this.dialog
      .open(BdrsFormDialogComponent, { data: { mode: 'add' }, autoFocus: false })
      .afterClosed()
      .pipe(filter((result): result is BdrsMappingRequest => !!result))
      .subscribe((result) => {
        this.infrastructureService.createBdrsEntry(result).subscribe({
          next: () => {
            this.notificationService.showSuccess('dsaManagement.infrastructure.notifications.created');
            this.loadBdrs();
          },
          error: () =>
            this.notificationService.showError('dsaManagement.infrastructure.notifications.createError')
        });
      });
  }

  editBdrs(entry: BdrsEntry): void {
    this.dialog
      .open(BdrsFormDialogComponent, { data: { mode: 'edit', entry }, autoFocus: false })
      .afterClosed()
      .pipe(filter((result): result is BdrsMappingRequest => !!result))
      .subscribe((result) => {
        this.infrastructureService.updateBdrsEntry(entry.bpn, result).subscribe({
          next: () => {
            this.notificationService.showSuccess('dsaManagement.infrastructure.notifications.updated');
            this.loadBdrs();
          },
          error: () =>
            this.notificationService.showError('dsaManagement.infrastructure.notifications.updateError')
        });
      });
  }

  deleteBdrs(entry: BdrsEntry): void {
    this.confirmDelete(entry.bpn).subscribe(() => {
      this.infrastructureService.deleteBdrsEntry(entry.bpn).subscribe({
        next: () => {
          this.notificationService.showSuccess('dsaManagement.infrastructure.notifications.deleted');
          this.loadBdrs();
        },
        error: () =>
          this.notificationService.showError('dsaManagement.infrastructure.notifications.deleteError')
      });
    });
  }

  /** Opens the shared confirmation dialog with the lifecycle warning and emits once on confirm. */
  private confirmDelete(name: string) {
    const data: ConfirmationDialogData = {
      title: this.transloco.translate('dsaManagement.infrastructure.delete.title'),
      message: this.transloco.translate('dsaManagement.infrastructure.delete.message', { name }),
      confirmText: this.transloco.translate('dsaManagement.infrastructure.delete.confirm'),
      cancelText: this.transloco.translate('dsaManagement.infrastructure.delete.cancel'),
      variant: 'danger',
      iconVariant: 'danger',
      icon: 'delete'
    };
    return this.dialog
      .open(ConfirmationDialogComponent, { data, width: '480px', autoFocus: false })
      .afterClosed()
      .pipe(filter((confirmed) => confirmed === true));
  }

  private initColumns(): void {
    const t = (key: string) =>
      this.transloco.translate(`dsaManagement.infrastructure.columns.${key}`);
    const action = this.transloco.translate('dsaManagement.infrastructure.columns.actions');

    this.discoveryColumns = [
      {
        name: 'organizationName',
        columnType: ColumnType.PLAIN,
        headerLabel: t('organizationName'),
        width: '24%'
      },
      { name: 'bpnl', columnType: ColumnType.PLAIN, headerLabel: t('bpnl'), width: '20%' },
      {
        name: 'connectorUrl',
        columnType: ColumnType.PLAIN,
        headerLabel: t('protocolUrl'),
        width: '26%',
        cellContent: (row) => row.connectorUrl ?? '—'
      },
      {
        name: 'useCases',
        columnType: ColumnType.PLAIN,
        headerLabel: t('useCases'),
        width: '18%',
        cellContent: (row) => (row.useCases.length > 0 ? row.useCases.join(', ') : '—')
      },
      {
        name: 'actions',
        columnType: ColumnType.TEMPLATE,
        headerLabel: action,
        width: '12%',
        template: this.discoveryActionTemplate()
      }
    ];

    this.bdrsColumns = [
      { name: 'bpn', columnType: ColumnType.PLAIN, headerLabel: t('bpn'), width: '32%' },
      { name: 'did', columnType: ColumnType.PLAIN, headerLabel: t('did'), width: '54%' },
      {
        name: 'actions',
        columnType: ColumnType.TEMPLATE,
        headerLabel: action,
        width: '14%',
        template: this.bdrsActionTemplate()
      }
    ];

    this.walletColumns = [
      { name: 'bpn', columnType: ColumnType.PLAIN, headerLabel: t('bpn'), width: '30%' },
      { name: 'did', columnType: ColumnType.PLAIN, headerLabel: t('did'), width: '50%' },
      {
        name: 'keyType',
        columnType: ColumnType.PLAIN,
        headerLabel: t('keyType'),
        width: '20%',
        cellContent: (row) => row.keyType ?? '—'
      }
    ];
  }
}
